import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { syncPendingSubscriptions } from '../utils/notificationHelper';
import { setUserContext, clearUserContext } from '../utils/sentryErrorHandler';
import { identifyUser, resetUser } from '../lib/analytics';
import { getCaptchaToken } from '../lib/turnstile';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, phone?: string) => Promise<void>;
  continueAsGuest: (phone: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Function to schedule token refresh
  const scheduleTokenRefresh = useCallback((session: Session | null) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (!session) return;

    // Get token expiry time
    const expiresAt = session.expires_at;
    if (!expiresAt) return;

    // Calculate when to refresh (5 minutes before expiry)
    const expiresIn = expiresAt * 1000 - Date.now();
    const refreshIn = Math.max(0, expiresIn - 5 * 60 * 1000); // 5 minutes before expiry

    if (refreshIn > 0) {
      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) throw error;
          
          if (data.session) {
            setSession(data.session);
            setUser(data.session.user);
            // Schedule next refresh
            scheduleTokenRefresh(data.session);
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          toast.error('Session expired. Please sign in again.');
          await supabase.auth.signOut();
        }
      }, refreshIn);
    }
  }, []);

  // Manual session refresh function
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
        scheduleTokenRefresh(data.session);
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      throw error;
    }
  }, [scheduleTokenRefresh]);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Schedule token refresh if we have a session
      if (session) {
        scheduleTokenRefresh(session);
      }
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const newUser = session?.user ?? null;
      setSession(session);
      setUser(newUser);
      
      // Schedule token refresh for new session
      if (session) {
        scheduleTokenRefresh(session);
      }
      
      // Handle different auth events
      switch (event) {
        case 'SIGNED_IN':
          // Anonymous (guest) sessions show their own checkout feedback — no generic toast.
          if (!session?.user?.is_anonymous) {
            toast.success('Signed in successfully!');
          }
          break;
        case 'SIGNED_OUT':
          toast.success('Signed out successfully!');
          break;
        case 'TOKEN_REFRESHED':
          console.log('Token refreshed successfully');
          break;
        case 'USER_UPDATED':
          console.log('User data updated');
          break;
      }
      
      // If user has logged in, sync any pending push subscriptions and set Sentry user context
      if (newUser) {
        // Identify before the await so the session is attributable even if
        // subscription syncing throws.
        identifyUser(newUser.id, {
          email: newUser.email,
          isGuest: !!newUser.is_anonymous,
        });
        try {
          await syncPendingSubscriptions();
          // Set Sentry user context
          setUserContext(newUser.id, newUser.email);
        } catch (error) {
          console.error('Error syncing subscriptions:', error);
        }
      } else {
        // Clear Sentry user context on logout
        clearUserContext();
        resetUser();
      }
    });

    // Track user activity for session management
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Add activity listeners
    window.addEventListener('click', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('mousemove', handleActivity);

    return () => {
      subscription.unsubscribe();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
    };
  }, [scheduleTokenRefresh]);

  const signUp = async (email: string, password: string, phone?: string) => {
    try {
      // Create the user account (Cloudflare Turnstile token required — CAPTCHA is enabled
      // project-wide in Supabase Auth).
      const captchaToken = await getCaptchaToken();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { captchaToken },
      });
      
      if (error) throw error;
      
      // Manually create a profile for the user if needed
      if (data.user) {
        // Check if profile exists first
        const { data: profileData, error: profileCheckErr } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();
          
        // Create profile only if it doesn't exist
        if (!profileData && !profileCheckErr) {
          const profileInsert: any = {
            id: data.user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          // Add phone number if provided
          if (phone && phone.trim()) {
            profileInsert.phone = phone.trim();
          }
          
          const { error: insertErr } = await supabase
            .from('profiles')
            .insert(profileInsert);
            
          // Continue even if profile creation fails, since it's not critical
        }
      }
      
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign up');
      throw error;
    }
  };

  // Frictionless guest checkout: create an anonymous auth user (real user_id, no email) so
  // orders/loyalty/RLS keep working, then write the phone (+ optional name) to the profile row
  // that the handle_new_user trigger creates. No password, no email, no SMS verification.
  const continueAsGuest = async (phone: string, name?: string) => {
    try {
      const captchaToken = await getCaptchaToken();
      const { data, error } = await supabase.auth.signInAnonymously({
        options: { captchaToken },
      });
      if (error) throw error;

      if (data.user) {
        // `any` matches the pattern in signUp() and avoids the strict generated profiles type
        // (which doesn't include every column we set here).
        const profileRow: any = {
          id: data.user.id,
          phone: phone.trim(),
          updated_at: new Date().toISOString(),
        };
        if (name && name.trim()) profileRow.preferred_name = name.trim();
        // Carry any pre-login age verification (AgeGate stores it in sessionStorage) onto the
        // profile so the now-authenticated guest isn't re-gated.
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('age_verified') === 'true') {
          profileRow.age_verified = true;
        }
        // upsert: the trigger usually created the row already, but tolerate either state.
        const { error: profileErr } = await supabase
          .from('profiles')
          .upsert(profileRow, { onConflict: 'id' });
        if (profileErr) console.error('Failed to save guest profile:', profileErr);

        // onAuthStateChange already identified this anon user by id; enrich it with
        // the phone/name captured here so support can reach a guest who hit a problem
        // without needing their keystrokes recorded.
        identifyUser(data.user.id, {
          isGuest: true,
          phone: phone.trim(),
          name: name?.trim() || undefined,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to continue');
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const captchaToken = await getCaptchaToken();
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) throw error;
      // Success toast is now handled in onAuthStateChange
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign in');
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Success toast is now handled in onAuthStateChange
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign out');
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, continueAsGuest, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}